import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { postJson } from '../../utils/apiClient';

const STEP_EMAIL = 1;
const STEP_OTP = 2;
const STEP_PASSWORD = 3;
const STEP_DONE = 4;

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // ─── Step 1: Send OTP ─────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const data = await postJson('crm/forgot_password_api.php', {
        action: 'send_otp',
        email: email.trim(),
      });

      setMessage(data.message || 'OTP sent to your email');
      if (data.reset_token) {
        setResetToken(data.reset_token);
      }
      setStep(STEP_OTP);
      toast.success('OTP sent! Check your email.');
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
      toast.error(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 2: Verify OTP ───────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const data = await postJson('crm/forgot_password_api.php', {
        action: 'verify_otp',
        email: email.trim(),
        otp: otp.trim(),
      });

      setMessage(data.message || 'OTP verified');
      if (data.reset_token) {
        setResetToken(data.reset_token);
      }
      setStep(STEP_PASSWORD);
      toast.success('OTP verified! Now set your new password.');
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP');
      toast.error(err.message || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 3: Reset Password ───────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const data = await postJson('crm/forgot_password_api.php', {
        action: 'reset_password',
        reset_token: resetToken,
        new_password: newPassword,
      });

      setMessage(data.message || 'Password reset successful!');
      setStep(STEP_DONE);
      toast.success('Password reset successful! You can now login.');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // ─── Resend OTP ───────────────────────────────────────────
  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const data = await postJson('crm/forgot_password_api.php', {
        action: 'send_otp',
        email: email.trim(),
      });

      setMessage(data.message || 'OTP resent to your email');
      if (data.reset_token) {
        setResetToken(data.reset_token);
      }
      toast.success('OTP resent!');
    } catch (err) {
      setError(err.message || 'Please wait before resending');
      toast.error(err.message || 'Please wait before resending');
    } finally {
      setLoading(false);
    }
  };

  // ─── Back to email step ───────────────────────────────────
  const handleBackToEmail = () => {
    setStep(STEP_EMAIL);
    setOtp('');
    setError('');
    setMessage('');
    setResetToken('');
  };

  // ─── Render: Step 1 - Email ───────────────────────────────
  const renderEmailStep = () => (
    <form onSubmit={handleSendOtp} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400">✉️</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="you@example.com"
            required
            disabled={loading}
          />
        </div>
      </div>

      <button
        type="submit"
        className="w-full px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={loading}
      >
        {loading ? 'Sending OTP...' : 'Send OTP'}
      </button>
    </form>
  );

  // ─── Render: Step 2 - OTP ─────────────────────────────────
  const renderOtpStep = () => (
    <form onSubmit={handleVerifyOtp} className="space-y-5">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          An OTP has been sent to <strong>{email}</strong>. 
          Please check your inbox (and spam folder).
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Enter OTP
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400">🔑</span>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-2xl tracking-[0.5em] font-mono"
            placeholder="000000"
            maxLength={6}
            required
            disabled={loading}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">Enter the 6-digit code sent to your email</p>
      </div>

      <button
        type="submit"
        className="w-full px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={loading || otp.length !== 6}
      >
        {loading ? 'Verifying...' : 'Verify OTP'}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={handleBackToEmail}
          className="text-gray-600 hover:text-gray-800 underline"
          disabled={loading}
        >
          Change email
        </button>
        <button
          type="button"
          onClick={handleResendOtp}
          className="text-blue-600 hover:text-blue-700 underline"
          disabled={loading}
        >
          Resend OTP
        </button>
      </div>
    </form>
  );

  // ─── Render: Step 3 - New Password ────────────────────────
  const renderPasswordStep = () => (
    <form onSubmit={handleResetPassword} className="space-y-5">
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-800">
          OTP verified! Now set your new password for <strong>{email}</strong>.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          New Password
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400">🔒</span>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Enter new password"
            minLength={6}
            required
            disabled={loading}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Confirm Password
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400">🔒</span>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Confirm new password"
            minLength={6}
            required
            disabled={loading}
          />
        </div>
      </div>

      {newPassword && confirmPassword && newPassword !== confirmPassword && (
        <p className="text-red-500 text-sm">Passwords do not match</p>
      )}

      <button
        type="submit"
        className="w-full px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={loading || !newPassword || !confirmPassword}
      >
        {loading ? 'Resetting Password...' : 'Reset Password'}
      </button>
    </form>
  );

  // ─── Render: Step 4 - Done ────────────────────────────────
  const renderDoneStep = () => (
    <div className="text-center space-y-5">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <span className="text-3xl">✅</span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">Password Reset Successful!</h2>
      <p className="text-gray-600">
        Your password has been updated. You can now login with your new password.
      </p>
      <button
        onClick={() => navigate('/login')}
        className="w-full px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl"
      >
        Go to Login
      </button>
    </div>
  );

  // ─── Steps indicator ──────────────────────────────────────
  const renderStepsIndicator = () => {
    if (step === STEP_DONE) return null;

    const steps = [
      { num: 1, label: 'Email', active: step >= STEP_EMAIL },
      { num: 2, label: 'OTP', active: step >= STEP_OTP },
      { num: 3, label: 'Password', active: step >= STEP_PASSWORD },
    ];

    return (
      <div className="flex items-center justify-center mb-8 space-x-2">
        {steps.map((s, idx) => (
          <React.Fragment key={s.num}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
              s.active
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-500'
            }`}>
              {s.num}
            </div>
            <span className={`text-sm ${s.active ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
              {s.label}
            </span>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 w-8 ${step > s.num ? 'bg-blue-600' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden p-8 md:p-10">
          {/* Logo */}
          <div className="mb-6 text-center">
            <Link to="/" className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-lg">C</span>
              </div>
              <span className="text-2xl font-bold">Talencar</span>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 mt-4">
              {step === STEP_EMAIL && 'Forgot Password'}
              {step === STEP_OTP && 'Verify OTP'}
              {step === STEP_PASSWORD && 'Set New Password'}
              {step === STEP_DONE && 'Done!'}
            </h1>
            <p className="text-gray-600 mt-1">
              {step === STEP_EMAIL && 'Enter your email to receive an OTP'}
              {step === STEP_OTP && 'Enter the 6-digit code sent to your email'}
              {step === STEP_PASSWORD && 'Choose a new password for your account'}
            </p>
          </div>

          {renderStepsIndicator()}

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Success message */}
          {message && step !== STEP_DONE && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{message}</p>
            </div>
          )}

          {/* Step content */}
          {step === STEP_EMAIL && renderEmailStep()}
          {step === STEP_OTP && renderOtpStep()}
          {step === STEP_PASSWORD && renderPasswordStep()}
          {step === STEP_DONE && renderDoneStep()}

          {/* Back to login */}
          {step !== STEP_DONE && (
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-gray-600 text-sm">
                Remember your password?{' '}
                <Link to="/login" className="text-blue-600 font-semibold hover:text-blue-700">
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

